import React, { useRef, useState } from "react";

const MAX_DURATION = 30 * 60; // 30 minutes

export default function VideoRecorder() {
  const videoRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [videoURL, setVideoURL] = useState(null);
  const [videoBlob, setVideoBlob] = useState(null);

  const [spo2, setSpo2] = useState("");
  const [sys, setSys] = useState("");
  const [dia, setDia] = useState("");

  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("");

  /* ================== RECORDING ================== */

  const startRecording = async () => {
    const stream = await navigator.mediaDevices.getUserMedia({
      video: { width: 640, height: 480 },
      audio: false,
    });

    videoRef.current.srcObject = stream;

    recorderRef.current = new MediaRecorder(stream, {
      mimeType: "video/webm",
    });

    chunksRef.current = [];

    recorderRef.current.ondataavailable = (e) => {
      if (e.data.size > 0) chunksRef.current.push(e.data);
    };

    recorderRef.current.start();
    setRecording(true);
    setSeconds(0);

    timerRef.current = setInterval(() => {
      setSeconds((prev) => {
        if (prev >= MAX_DURATION) stopRecording();
        return prev + 1;
      });
    }, 1000);
  };

  const stopRecording = () => {
    clearInterval(timerRef.current);
    recorderRef.current.stop();

    videoRef.current?.srcObject?.getTracks().forEach((t) => t.stop());

    recorderRef.current.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: "video/webm" });
      setVideoBlob(blob);
      setVideoURL(URL.createObjectURL(blob));
    };

    setRecording(false);
  };

  /* ================== SUBMIT ================== */

  const submitData = async () => {
    if (!videoBlob || !spo2 || !sys || !dia || !consent) {
      alert("Please complete all fields and give consent.");
      return;
    }

    try {
      setLoading(true);
      setStatus("");

      const formData = new FormData();
      formData.append("video", videoBlob, "face_video.webm");
      formData.append("spo2", spo2);
      formData.append("sbp", sys);
      formData.append("dbp", dia);

      const response = await fetch(
        "https://www.careplus.gopillz.com/upload-vitals",
        {
          method: "POST",
          body: formData,
        }
      );

      if (!response.ok) throw new Error("Upload failed");

      setStatus("✅ Data uploaded successfully");
    } catch (e) {
      setStatus("❌ Upload failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  /* ================== UI ================== */

  return (
    <div className="card glass">
      <h2>Doctor Video Capture</h2>

      <video ref={videoRef} autoPlay muted className="video" />

      <div className="controls">
        {!recording ? (
          <button className="btn primary" onClick={startRecording}>
            ▶ Start Recording
          </button>
        ) : (
          <button className="btn danger" onClick={stopRecording}>
            ⏹ Stop Recording
          </button>
        )}

        <span className="timer">
          {Math.floor(seconds / 60)}:{("0" + (seconds % 60)).slice(-2)}
        </span>
      </div>

      {videoURL && <video src={videoURL} controls className="video" />}

      {/* ================= DOCTOR INPUT ================= */}
      <div className="form">
        <h3>Doctor Input</h3>

        {/* SpO2 */}
        <label>Oxygen Saturation (SpO₂)</label>
        <input
          type="text"
          inputMode="numeric"
          maxLength={2}
          placeholder="95"
          value={spo2}
          onChange={(e) => {
            const v = e.target.value.replace(/\D/g, "");
             setSpo2(v);
          }}
        />

        {/* Blood Pressure */}
        <label>Blood Pressure (mmHg)</label>
        <div className="bp">
          <input
            type="text"
            inputMode="numeric"
            maxLength={3}
            placeholder="Systolic"
            value={sys}
            onChange={(e) =>
              setSys(e.target.value.replace(/\D/g, "").slice(0, 3))
            }
          />
          <span>/</span>
          <input
            type="text"
            inputMode="numeric"
            maxLength={3}
            placeholder="Diastolic"
            value={dia}
            onChange={(e) =>
              setDia(e.target.value.replace(/\D/g, "").slice(0, 3))
            }
          />
        </div>

        {/* Consent */}
        <div className="consent">
          <input
            type="checkbox"
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
          />
          <span>
            I confirm the patient has given consent for video capture and vitals
            submission.
          </span>
        </div>

        <button
          className="btn primary full"
          onClick={submitData}
          disabled={loading || !consent}
        >
          {loading ? "Uploading..." : "Submit"}
        </button>

        {status && <p className="status">{status}</p>}
      </div>
    </div>
  );
}
